import { isAxiosError } from "axios";
import { bulkUpdateTransactionsPartialFailureSchema } from "@common/schemas/transaction.schema";

export interface BulkUpdateFailureCounts {
    succeededCount: number;
    /**
     * `null` when the failing request did not report how many rows it refused,
     * or when later requests were never sent.
     */
    failedCount: number | null;
}

/**
 * A bulk update stops at the first failing request, so earlier requests and
 * part of the failing one may already be saved. Carries what did land.
 */
export class BulkUpdateFailureError extends Error {
    readonly succeededCount: number;
    readonly failedCount: number | null;

    constructor({
        succeededCount,
        failedCount,
        message,
        cause,
    }: BulkUpdateFailureCounts & { message: string; cause?: Error }) {
        super(message, { cause });
        this.name = "BulkUpdateFailureError";
        this.succeededCount = succeededCount;
        this.failedCount = failedCount;
    }
}

export function buildBulkUpdateFailureError({
    cause,
    completedSucceededCount,
    unsentRequestCount,
}: {
    cause: Error | undefined;
    completedSucceededCount: number;
    unsentRequestCount: number;
}): BulkUpdateFailureError {
    const partialFailure = isAxiosError(cause)
        ? bulkUpdateTransactionsPartialFailureSchema.safeParse(
              cause.response?.data,
          )
        : undefined;
    if (!partialFailure?.success) {
        return new BulkUpdateFailureError({
            succeededCount: completedSucceededCount,
            failedCount: null,
            message: cause?.message ?? "Bulk update failed",
            cause,
        });
    }
    return new BulkUpdateFailureError({
        succeededCount:
            completedSucceededCount + partialFailure.data.succeededCount,
        failedCount:
            unsentRequestCount > 0 ? null : partialFailure.data.failedCount,
        message: partialFailure.data.message,
        cause,
    });
}
